const STORAGE_KEY = "recently_accessed_customers";
const MAX_RECENT_CUSTOMERS = 15;

export interface IRecentlyAccessedCustomer {
  customer_id: number | string;
  customer_name: string;
  customer_code: string;
  email?: string;
  phone?: string;
  mobile?: string;
  accessed_at: number;
}

/**
 * Reads the list of recently accessed customers from localStorage,
 * ordered from most recently accessed to least.
 */
export const getRecentlyAccessedCustomers = (): IRecentlyAccessedCustomer[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as IRecentlyAccessedCustomer[];
  } catch {
    return [];
  }
};

/**
 * Records a customer as recently accessed. The most recent access is moved
 * to the front, duplicates (by customer_id) are removed, and the list is
 * capped at MAX_RECENT_CUSTOMERS entries.
 */
export const addRecentlyAccessedCustomer = (
  customer: Omit<IRecentlyAccessedCustomer, "accessed_at">
): void => {
  if (customer?.customer_id === undefined || customer?.customer_id === null) {
    return;
  }
  try {
    const existing = getRecentlyAccessedCustomers().filter(
      (c) => String(c.customer_id) !== String(customer.customer_id)
    );
    const updated: IRecentlyAccessedCustomer[] = [
      { ...customer, accessed_at: Date.now() },
      ...existing,
    ].slice(0, MAX_RECENT_CUSTOMERS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage failures (e.g., quota exceeded, disabled storage)
  }
};

/**
 * Clears all recently accessed customers.
 */
export const clearRecentlyAccessedCustomers = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures
  }
};
